"use client"

import { useEffect, useRef, useState } from "react"
import { action_getAccessoryProducts } from "src/actions/checkout"
import { useCartState } from "src/checkout/hooks/useCart"
import { ResolvedAccessoryProductData } from "src/data/product-accessories-resolved/types"
import { createAsyncGlobalState } from "src/hooks/asyncGlobalState"
import { StorefrontCart } from "src/types/storefront"

const accessoriesProductsStore = createAsyncGlobalState({
  initialize: () => action_getAccessoryProducts(),
  initialValue: [],
})

// https://elvent.shop/wp-admin/admin.php?page=wc-admin&path=%2Fanalytics%2Fcategories&filter=single_category&categories=537&orderby=net_revenue&order=desc
const mostPopuparFirst = [
  "Regen- und Windschutz",
  "Insektenschutz",
  "Sitzerhöhung",
  "Isolierte Snack- und Getränketasche",
  "Schattennetz",
  "Kühltasche",
  "Gepäckhaken",
  "Smartphonehalter",
]

function getPopularity(accessory: ResolvedAccessoryProductData) {
  for (let i = 0; i < mostPopuparFirst.length; i++) {
    const text = mostPopuparFirst[i]!

    if (accessory.name.toLowerCase().includes(text.toLowerCase())) {
      return i
    }
  }

  return Number.MAX_SAFE_INTEGER
}

function compareByPopularity(a: ResolvedAccessoryProductData, b: ResolvedAccessoryProductData) {
  const aIndex = getPopularity(a)
  const bIndex = getPopularity(b)

  return aIndex - bIndex
}

function buildRecommendedAccessories(cart: StorefrontCart, accessories: ResolvedAccessoryProductData[]) {
  const accessoriesBySlug = new Map<string, ResolvedAccessoryProductData[]>()
  const recommendedBySku = new Map<string, ResolvedAccessoryProductData>()

  for (const accessory of accessories) {
    const array = accessoriesBySlug.get(accessory.slug) || []
    array.push(accessory)
    accessoriesBySlug.set(accessory.slug, array)
  }

  for (const item of cart.items) {
    for (const slug of item.product.accessorySlugs || []) {
      const accessories = accessoriesBySlug.get(slug)

      if (!accessories) {
        continue
      }

      const resolvedAccessory =
        accessories.length > 1
          ? accessories.find((accessory) => accessory.variationName === item.product.variationName)
          : accessories[0]

      if (accessories.length > 1 && !resolvedAccessory) {
        console.warn("Cart item accessory has variations but none matched", { item, accessories })
      }

      if (resolvedAccessory) {
        recommendedBySku.set(resolvedAccessory.sku, resolvedAccessory)
      }
    }
  }

  const recommended = Array.from(recommendedBySku.values())

  return recommended.sort(compareByPopularity)
}

export function useRecommendedAccessories() {
  const { data: cart } = useCartState()
  const { data: accessories } = accessoriesProductsStore.use()
  const [recommended, setRecommended] = useState<ResolvedAccessoryProductData[]>([])
  const recommendedHashRef = useRef("")

  const recommendedHash =
    cart?.items.flatMap((item) => (item.product.accessorySlugs?.length ? item.product.slug : [])).join("|") || ""

  useEffect(() => {
    if (!cart || recommendedHash === recommendedHashRef.current) {
      return
    }

    const recommended = buildRecommendedAccessories(cart, accessories)

    if (recommended.length) {
      setRecommended(recommended)
    }

    recommendedHashRef.current = recommendedHash
  }, [cart, accessories, recommendedHash])

  return recommended
}
